import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  orderNumber: "",
  message: "",
  consent: false,
};

const subjectOptions = [
  "Product advice",
  "Order support",
  "Delivery and returns",
  "Warranty and repairs",
  "Something else",
];

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submissionTimer = useRef(null);
  const contactDetails = siteConfig.contact;

  useEffect(
    () => () => {
      if (submissionTimer.current) {
        window.clearTimeout(submissionTimer.current);
      }
    },
    []
  );

  const handleInputChange = (event) => {
    const { checked, name, type, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Enter your first name.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Enter your last name.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Choose a topic.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 20) {
      nextErrors.message = "Please provide at least 20 characters.";
    }
    if (!formData.consent) {
      nextErrors.consent = "Please confirm that we may respond to your enquiry.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setShowSuccess(false);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    submissionTimer.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);
    }, 800);
  };

  const fieldError = (name) =>
    errors[name] ? (
      <span className="contact-page__error" id={name + "-error"} role="alert">
        {errors[name]}
      </span>
    ) : null;

  return (
    <div className="contact-page">
      <nav className="contact-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Contact</span>
      </nav>

      <header className="contact-page__header">
        <p className="contact-page__eyebrow">Customer care</p>
        <h1>How can we help?</h1>
        <p>
          Product question, order update or advice for your next trip — our
          team will point you in the right direction.
        </p>
      </header>

      <main className="contact-page__main">
        <aside className="contact-page__aside" aria-label="Contact details">
          <div className="contact-page__contact-card">
            <p className="contact-page__card-label">Email</p>
            <a href={"mailto:" + contactDetails.email}>
              {contactDetails.email}
            </a>
            <p>For orders, products and general enquiries.</p>
          </div>
          <div className="contact-page__contact-card">
            <p className="contact-page__card-label">Write to us</p>
            <address>{contactDetails.address}</address>
          </div>
          <div className="contact-page__quick-links">
            <h2>Looking for a quick answer?</h2>
            <Link to="/shipping">Delivery information</Link>
            <Link to="/returns">Returns and refunds</Link>
          </div>
        </aside>

        <section className="contact-page__form-card" aria-labelledby="contact-form-title">
          <div className="contact-page__form-heading">
            <p className="contact-page__eyebrow">Send a message</p>
            <h2 id="contact-form-title">Tell us what you need</h2>
            <p>Required fields are marked with an asterisk.</p>
          </div>

          {showSuccess && (
            <div className="contact-page__success" role="status">
              <strong>Message received.</strong>
              <span>
                Thanks for contacting {siteConfig.shortName || siteConfig.brandName}.
                We will reply as soon as we can.
              </span>
            </div>
          )}

          <form aria-busy={isSubmitting} noValidate onSubmit={handleSubmit}>
            <div className="contact-page__form-row">
              <div className="contact-page__field">
                <label htmlFor="firstName">First name *</label>
                <input
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                  aria-invalid={Boolean(errors.firstName)}
                  id="firstName"
                  name="firstName"
                  onChange={handleInputChange}
                  value={formData.firstName}
                />
                {fieldError("firstName")}
              </div>
              <div className="contact-page__field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                  aria-invalid={Boolean(errors.lastName)}
                  id="lastName"
                  name="lastName"
                  onChange={handleInputChange}
                  value={formData.lastName}
                />
                {fieldError("lastName")}
              </div>
            </div>

            <div className="contact-page__form-row">
              <div className="contact-page__field">
                <label htmlFor="email">Email address *</label>
                <input
                  aria-describedby={errors.email ? "email-error" : undefined}
                  aria-invalid={Boolean(errors.email)}
                  autoComplete="email"
                  id="email"
                  name="email"
                  onChange={handleInputChange}
                  type="email"
                  value={formData.email}
                />
                {fieldError("email")}
              </div>
            </div>

            <div className="contact-page__form-row">
              <div className="contact-page__field">
                <label htmlFor="subject">Topic *</label>
                <select
                  aria-describedby={errors.subject ? "subject-error" : undefined}
                  aria-invalid={Boolean(errors.subject)}
                  id="subject"
                  name="subject"
                  onChange={handleInputChange}
                  value={formData.subject}
                >
                  <option value="">Choose a topic</option>
                  {subjectOptions.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
                {fieldError("subject")}
              </div>
              <div className="contact-page__field">
                <label htmlFor="orderNumber">Order number</label>
                <input
                  id="orderNumber"
                  name="orderNumber"
                  onChange={handleInputChange}
                  placeholder="If your enquiry is about an order"
                  value={formData.orderNumber}
                />
              </div>
            </div>

            <div className="contact-page__field">
              <label htmlFor="message">Message *</label>
              <textarea
                aria-describedby={errors.message ? "message-error" : "message-hint"}
                aria-invalid={Boolean(errors.message)}
                id="message"
                name="message"
                onChange={handleInputChange}
                rows="7"
                value={formData.message}
              />
              {!errors.message && (
                <span className="contact-page__hint" id="message-hint">
                  Include product names, dates or order details where helpful.
                </span>
              )}
              {fieldError("message")}
            </div>

            <div className="contact-page__consent">
              <input
                aria-describedby={errors.consent ? "consent-error" : undefined}
                aria-invalid={Boolean(errors.consent)}
                checked={formData.consent}
                id="consent"
                name="consent"
                onChange={handleInputChange}
                type="checkbox"
              />
              <label htmlFor="consent">
                I agree that my details may be used to respond to this enquiry.
                See our <Link to="/privacy">privacy policy</Link>. *
              </label>
            </div>
            {fieldError("consent")}

            <button disabled={isSubmitting} type="submit">
              {isSubmitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}

export default Contact;
